import { useMemo } from 'react';

import { useDeepCompareMemo } from 'use-deep-compare';

import { useLangProvider } from '@/components/core/AppLayout';
import { coins } from '@/layers/core/data/country';

import { idToCountry } from '../../domain/countryMap';

import { useCountry } from './CountryContext';

export const useCurrencyUtils = (
  countryId?: number,
  formatterOptions: Intl.NumberFormatOptions = {}
) => {
  const { country: countryAbbr } = useCountry();
  const country = countryId
    ? idToCountry[countryId as 1] ?? countryAbbr
    : countryAbbr;
  const language = useLangProvider();

  const currency = coins[country];
  const locale = `${language}-${country}`;
  const fmtOptions = useDeepCompareMemo(
    () => formatterOptions,
    [formatterOptions]
  );
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        ...fmtOptions,
        style: 'currency',
        currency,
      }),
    [currency, fmtOptions, locale]
  );

  const decimalSeparator = useMemo(() => {
    const parts = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
    }).formatToParts(1_234.56);
    return (
      parts.find((part) => part.type === 'decimal')?.value ??
      new Error("Can't find decimal separator")
    );
  }, [locale]);

  // it should always find the decimal separator
  if (decimalSeparator instanceof Error) throw decimalSeparator;

  // to check if a number ends with a decimal or .0, etc.
  const numberEndings = useMemo(
    () =>
      Array.from({
        length: currencyFormatter.resolvedOptions().maximumFractionDigits ?? 2,
      }).map((_, i) => '.'.padEnd(i + 1, '0')),
    [currencyFormatter]
  );

  return useMemo(
    () => ({
      formatValue(value: string | number) {
        if (Number.isNaN(Number.parseFloat(value.toString()))) return value;
        const valueAsNumber =
          typeof value === 'string' ? Number.parseFloat(value) || 0 : value;
        const formattedParts = currencyFormatter.formatToParts(valueAsNumber);

        return `${formattedParts // filter out the currency symbol and empty spaces, and join the rest
          .filter((p) => p.type !== 'literal' && p.type !== 'currency')
          .map((p) => p.value)
          // put the decimal separator and insignificant zeroes back in if the value ends with it
          .join('')}${
          numberEndings
            .find((e) => value.toString().endsWith(e))
            ?.replace('.', decimalSeparator) || ''
        }`;
      },
      cleanValue(
        value: string,
        options?: {
          integer?: boolean;
          positive?: boolean;
        }
      ) {
        const [integer, decimal] = value.split(decimalSeparator);
        // remove all non-numbers from the integer part
        const sign = integer.startsWith('-') ? '-' : '';
        const cleanInt = integer.replaceAll(/[^\d]/g, '');
        const decimalPart =
          typeof decimal === 'string' // if there's a decimal part, add it back
            ? `.${decimal.replaceAll(/[^\d]/g, '')}`
            : '';

        return `${options?.positive ? '' : sign}${cleanInt}${
          options?.integer ? '' : decimalPart
        }`;
      },
      formatNumber(value: string | number) {
        if (Number.isNaN(Number.parseFloat(value.toString()))) return value;
        return `${value}`.replace('.', decimalSeparator);
      },
      formatter: currencyFormatter,
      limitToMaxValue,
      limitToMinValue,
      locale,
    }),
    [decimalSeparator, currencyFormatter, locale, numberEndings]
  );
};

function limitToMaxValue<T extends string | number>(
  value: T,
  max = 9_999_999
): T {
  const valueAsNumber = Number.parseFloat(value.toString());
  if (Number.isNaN(valueAsNumber) || max >= valueAsNumber) return value;

  return (typeof value === 'string' ? max.toString() : max) as T;
}

export function limitToMinValue<T extends string | number>(
  value: T,
  min = 0
): T {
  const valueAsNumber = Number.parseFloat(value.toString());
  if (Number.isNaN(valueAsNumber) || min <= valueAsNumber) return value;

  return (typeof value === 'string' ? min.toString() : min) as T;
}
